/**
 * Project recalc trigger — Projects v2 Phase 9a (P9a.3).
 *
 * The debounced client-side recalc scheduler. Lives in its own module
 * (not in project-cost-rollup.service.ts) so the cost-input write
 * services can import `scheduleRecalc` without forming an import cycle:
 * cost-rollup re-exports computeRevisionDelta from project-plan.service,
 * and several of those write services live in project-plan.service —
 * a static import back into cost-rollup would close the loop. We break
 * it with a lazy `import()` of recalculateProject inside the debounce
 * callback (runtime, not module-load).
 *
 * Debounce model: 2s per-project window. A burst of edits coalesces into
 * a single recalc. Fire-and-forget — never blocks or throws into the
 * originating write.
 *
 * The in-memory Map survives only for the tab's lifetime. Anything
 * dropped when the tab closes mid-debounce is caught by the P9a.4
 * Cloud Function safety-net.
 */

import type { ProjectCostRollup } from '@/types/project.types';

type TriggeredBy = ProjectCostRollup['lastRecalcTriggeredBy'];

const DEBOUNCE_MS = 2000;
const pendingRecalcs = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Feature flag — flip to false if recalc thrash becomes a production
 * problem. When false, scheduleRecalc + flushRecalc become no-ops and
 * the manual Recalculate button + the P9a.4 Cloud Function safety-net
 * are the only recalc paths.
 */
export const COST_ROLLUP_CLIENT_TRIGGERS_ENABLED = true;

/**
 * Schedule a debounced recalc for a project. Coalesces repeated calls
 * within DEBOUNCE_MS into one. Always safe to call — swallows every
 * error so a recalc failure can't break the triggering write.
 */
export function scheduleRecalc(
  projectId: string | null | undefined,
  triggeredBy: TriggeredBy = 'client-debounced'
): void {
  if (!COST_ROLLUP_CLIENT_TRIGGERS_ENABLED) return;
  if (!projectId) return;

  const existing = pendingRecalcs.get(projectId);
  if (existing) clearTimeout(existing);

  const handle = setTimeout(() => {
    pendingRecalcs.delete(projectId);
    // Lazy import breaks the cost-rollup ↔ plan import cycle. By the
    // time this fires (≥2s after a write), both modules are loaded.
    import('./project-cost-rollup.service')
      .then(({ recalculateProject }) =>
        recalculateProject(projectId, { triggeredBy })
      )
      .catch((err) => {
        console.warn(
          `[project-recalc-trigger] debounced recalc failed for ${projectId}:`,
          err
        );
      });
  }, DEBOUNCE_MS);

  pendingRecalcs.set(projectId, handle);
}

/**
 * Cancel any pending debounce + run the recalc immediately. Used by
 * bulk-import flows that want the roll-up fresh the moment the batch
 * lands. Errors are surfaced (unlike scheduleRecalc) so an explicit
 * flush can report failure.
 */
export async function flushRecalc(
  projectId: string,
  triggeredBy: TriggeredBy = 'manual'
): Promise<void> {
  const existing = pendingRecalcs.get(projectId);
  if (existing) {
    clearTimeout(existing);
    pendingRecalcs.delete(projectId);
  }
  if (!COST_ROLLUP_CLIENT_TRIGGERS_ENABLED) return;
  const { recalculateProject } = await import('./project-cost-rollup.service');
  await recalculateProject(projectId, { triggeredBy });
}
